import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, KeyRound, Loader2 } from 'lucide-react';
import PasswordInput from './PasswordInput';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const strengthConfig = (score) => {
  if (score <= 1) return { msg: 'Weak', color: 'text-red-600', bar: 'bg-red-500' };
  if (score <= 3) return { msg: 'Fair', color: 'text-yellow-600', bar: 'bg-yellow-500' };
  if (score <= 4) return { msg: 'Good', color: 'text-green-600', bar: 'bg-green-500' };
  return { msg: 'Strong', color: 'text-green-600', bar: 'bg-green-500' };
};

const PasswordReqs = ({ pw }) => {
  const checks = [
    [pw.length >= 8, 'At least 8 characters'],
    [/[A-Z]/.test(pw) && /[a-z]/.test(pw), 'Upper & lowercase letters'],
    [/\d/.test(pw), 'At least one number'],
  ];
  return <div className="mt-2 space-y-1">{checks.map(([ok, label]) => <p key={label} className={`text-xs ${ok ? 'text-green-600' : 'text-gray-500'}`}>{ok ? '\u2713' : '\u25CB'} {label}</p>)}</div>;
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [pwScore, setPwScore] = useState(0);

  useEffect(() => { if (!token) setError('Invalid or missing reset token'); }, [token]);

  useEffect(() => {
    if (!newPassword) { setPwScore(0); return; }
    let s = 0;
    if (newPassword.length >= 8) s++;
    if (newPassword.length >= 12) s++;
    if (/[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)) s++;
    if (/\d/.test(newPassword)) s++;
    if (/[^a-zA-Z0-9]/.test(newPassword)) s++;
    setPwScore(s);
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (newPassword.length < 8) { setError('Password must be at least 8 characters long'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (pwScore < 2) { setError('Please choose a stronger password'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, new_password: newPassword }) });
      if (res.ok) { setSuccess(true); setTimeout(() => navigate('/login'), 3000); }
      else { let msg = 'Failed to reset password. The link may have expired.'; try { const d = await res.json(); if (d.detail) msg = d.detail; } catch (err) { logger.error('Password reset error:', err); } setError(msg); }
    } catch (err) { setError('Unable to connect. Please check your internet connection and try again.'); }
    finally { setLoading(false); }
  };

  const cfg = strengthConfig(pwScore);

  if (success) {
    return (
      <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-8 h-8 text-green-600" /></div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Password Reset Successful!</h2>
          <p className="text-gray-600 mb-6">Your password has been successfully reset. You can now log in with your new password.</p>
          <button onClick={() => navigate('/login')} className="w-full bg-linear-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 rounded-lg transition">Continue to Login</button>
          <p className="text-xs text-gray-500 mt-4">Redirecting automatically in 3 seconds...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <button onClick={() => navigate('/login')} className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition"><ArrowLeft className="w-5 h-5 mr-2" />Back to Login</button>
        <div className="w-16 h-16 bg-linear-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6"><KeyRound className="w-8 h-8 text-white" /></div>
        <h1 className="text-3xl font-bold text-center text-slate-900 mb-2">Set New Password</h1>
        <p className="text-gray-600 text-center mb-8">Choose a strong password for your account</p>
        {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"><p className="text-red-700 text-sm">{error}</p></div>}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
            <PasswordInput value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" name="new-password" autoComplete="new-password" />
            {newPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-600">Password Strength:</span><span className={`text-xs font-medium ${cfg.color}`}>{cfg.msg}</span></div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full transition-all duration-300 ${cfg.bar}`} style={{ width: `${(pwScore / 5) * 100}%` }} /></div>
              </div>
            )}
            {newPassword && <PasswordReqs pw={newPassword} />}
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
            <PasswordInput value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" name="confirm-password" autoComplete="new-password" />
            {confirmPassword && confirmPassword !== newPassword && <p className="text-xs text-red-600 mt-2">Passwords do not match</p>}
            {confirmPassword && confirmPassword === newPassword && <p className="text-xs text-green-600 mt-2">{'\u2713'} Passwords match</p>}
          </div>
          <button type="submit" disabled={loading || !token} className="w-full bg-linear-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? <span className="flex items-center justify-center"><Loader2 className="animate-spin h-5 w-5 mr-2" />Resetting...</span> : 'Reset Password'}
          </button>
        </form>
        <p className="text-xs text-gray-500 text-center mt-6">This link expires in 1 hour for security reasons.</p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
